import { useState } from "react";

const NEWSLETTER_URL = "https://readdy.ai/api/form/d6up6ie7ipaugi04lcqg";

const quickLinks = [
  { label: "Home", href: "#home" },
  { label: "About Us", href: "#about" },
  { label: "Our Plans", href: "#plans" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "FAQ", href: "#faq" },
  { label: "Contact Us", href: "#contact" },
];

const planLinks = [
  "Individual & Family Plans",
  "Medicare Solutions",
  "Supplemental Coverage",
  "Small Business Plans",
  "Dental & Vision",
];

export default function Footer() {
  const [nlStatus, setNlStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [nlEmail, setNlEmail] = useState("");

  const handleNav = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const handleNewsletter = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setNlStatus("sending");
    try {
      const body = new URLSearchParams({ email: nlEmail });
      const res = await fetch(NEWSLETTER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) { setNlStatus("success"); setNlEmail(""); }
      else setNlStatus("error");
    } catch {
      setNlStatus("error");
    }
  };

  return (
    <footer className="bg-emerald-800">
      {/* Newsletter strip */}
      <div className="border-b border-white/10 py-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div>
              <h4 className="font-['Poppins'] font-700 text-white text-xl mb-1">Stay Informed</h4>
              <p className="font-['Open_Sans'] text-white/60 text-sm">
                Get tips on health insurance, Medicare updates, and open enrollment reminders.
              </p>
            </div>
            {nlStatus === "success" ? (
              <div className="flex items-center gap-2 text-emerald-300 font-['Open_Sans'] text-sm">
                <i className="ri-checkbox-circle-fill text-lg"></i> You're subscribed! Thanks for joining.
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleNewsletter} id="newsletter-form" className="flex gap-3 w-full max-w-md">
                <input
                  type="email"
                  name="email"
                  required
                  value={nlEmail}
                  onChange={(e) => setNlEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="flex-1 px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/40 text-sm font-['Open_Sans'] focus:outline-none focus:border-amber-400 transition-colors"
                />
                <button
                  type="submit"
                  disabled={nlStatus === "sending"}
                  className="px-5 py-3 bg-amber-500 hover:bg-amber-400 disabled:bg-gray-500 text-white font-['Poppins'] font-600 text-sm rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  {nlStatus === "sending" ? "..." : "Subscribe"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="py-14">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-lg bg-amber-500 flex items-center justify-center">
                <i className="ri-heart-pulse-line text-white text-lg"></i>
              </div>
              <div>
                <div className="font-['Poppins'] font-700 text-white text-base leading-tight">HealthMarkets</div>
                <div className="font-['Open_Sans'] text-white/60 text-xs">Insurance Agency</div>
              </div>
            </div>
            <p className="font-['Open_Sans'] text-white/60 text-sm leading-relaxed mb-5">
              Licensed independent insurance agents serving Irving, TX and the Dallas-Fort Worth area. 
              We compare plans from 50+ top carriers to find you the best coverage.
            </p>
            <div className="flex gap-3">
              {["ri-facebook-fill", "ri-linkedin-fill", "ri-twitter-fill"].map((icon) => (
                <div key={icon} className="w-9 h-9 rounded-full bg-white/10 border border-white/20 flex items-center justify-center cursor-pointer hover:bg-amber-500 hover:border-amber-500 transition-colors">
                  <i className={`${icon} text-white text-sm`}></i>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h5 className="font-['Poppins'] font-600 text-white text-sm uppercase tracking-widest mb-5">Quick Links</h5>
            <ul className="space-y-2.5">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={(e) => handleNav(e, link.href)}
                    className="font-['Open_Sans'] text-white/60 text-sm hover:text-amber-400 transition-colors cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-arrow-right-s-line text-emerald-400 text-xs"></i>
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Insurance Plans */}
          <div>
            <h5 className="font-['Poppins'] font-600 text-white text-sm uppercase tracking-widest mb-5">Insurance Plans</h5>
            <ul className="space-y-2.5">
              {planLinks.map((plan) => (
                <li key={plan}>
                  <a
                    href="#plans"
                    onClick={(e) => handleNav(e, "#plans")}
                    className="font-['Open_Sans'] text-white/60 text-sm hover:text-amber-400 transition-colors cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-arrow-right-s-line text-emerald-400 text-xs"></i>
                    {plan}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h5 className="font-['Poppins'] font-600 text-white text-sm uppercase tracking-widest mb-5">Contact Info</h5>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-map-pin-2-line text-amber-400"></i>
                </div>
                <div>
                  <div className="font-['Open_Sans'] text-white/60 text-sm leading-relaxed">
                    1800 Market Pl Blvd #140<br />Irving, TX 75063
                  </div>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-phone-line text-amber-400"></i>
                </div>
                <a href="tel:9722174662" className="font-['Open_Sans'] text-white/60 text-sm hover:text-amber-400 transition-colors cursor-pointer">
                  972-217-4662
                </a>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className="ri-time-line text-amber-400"></i>
                </div>
                <span className="font-['Open_Sans'] text-emerald-300 text-sm font-600">Open 24/7</span>
              </li>
              <li>
                <a
                  href="https://maps.app.goo.gl/grzX3dievhN3giBv8"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-amber-400 text-xs font-['Open_Sans'] hover:text-amber-300 transition-colors cursor-pointer"
                >
                  <i className="ri-google-fill"></i> View on Google Maps
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10 py-6">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="font-['Open_Sans'] text-white/40 text-xs">
            &copy; {new Date().getFullYear()} HealthMarkets Insurance Agency. All rights reserved. Irving, TX 75063.
          </p>
          <div className="flex gap-5">
            {["Privacy Policy", "Terms of Service", "Accessibility"].map((item) => (
              <span key={item} className="font-['Open_Sans'] text-white/40 text-xs cursor-pointer hover:text-white/70 transition-colors">
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
